import { Button, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { renderClose } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { propBoolField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { propCurrency, propInt, propLength, propLengthMax, propMaxDecimalPlaces, propMaxValueExclusive, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { InventoryArticleDto, SaleArticleDto } from "./types";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { createListStore } from "../../common/listStore";
import { combineLatest } from "rxjs";

export const SaleArticleDialog = (props: {
    close: () => void;
    inventoryArticle: InventoryArticleDto;
    saleArticle: SaleArticleEditDto | null;
    onChange(): void;
}) => {
    const [useCodesStore] = useState(() => createListStore<string>({ path: "articles/saleArticles/codes" }));
    const [useStore] = useState(() => createEntityStore<SaleArticleEditDto>({
        data: newSaleArticle(props.inventoryArticle.id!),
        path: "articles/saleArticles",
        id: props.saleArticle != null ? props.saleArticle.id : null,
        getNewId: p => p.id!,
        onSaved() {
            props.onChange();
            props.close();
        }
    }));

    useEffect(() => {
        const sub = combineLatest([useStore.getState().serverData$, useCodesStore.getState().items$]).subscribe({
            next([{ data: { code } }, { items: codes }]) {
                useStore.getState().setPropValidators([
                    propRequired("code"),
                    propLength("code", 5, 5),
                    propValueValidator("code", p => p != null && p !== code && codes.some(r => r === p) && "Code already exists."),
                    propRequired("name"),
                    propLengthMax("name", 50),
                    propValidValue("price", "priceFormatted"), propRequired("price"), propMinValue("price", 0), propCurrency("price"),
                    propValidValue("vatPercent", "vatPercentFormatted"), propRequired("vatPercent"), propMaxDecimalPlaces("vatPercent", 2), propMinValue("vatPercent", 0), propMaxValueExclusive("vatPercent", 100),
                    propValidValue("moVatPercent", "moVatPercentFormatted"), propRequired("moVatPercent"), propMaxDecimalPlaces("moVatPercent", 2), propMinValue("moVatPercent", 0), propMaxValueExclusive("moVatPercent", 100),
                    propValidValue("baseQuantity", "baseQuantityFormatted"), propRequired("baseQuantity"), propMinValue("baseQuantity", 1), propInt("baseQuantity"),
                ]);
            },
        });
        return () => sub.unsubscribe();
    }, [useStore, useCodesStore]);

    const [useInventoryArticleStore] = useState(() => createEntityStore<InventoryArticleDto>({ data: props.inventoryArticle, id: props.inventoryArticle.id }));
    const disabled = !props.inventoryArticle.isPost;

    return <>
        <DialogTitle>{props.saleArticle?.id == null ? "New sales article" : props.inventoryArticle.isPost ? "Edit sales article" : "View sales article"}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2} alignItems="stretch">
                <Grid item xs={12}>{propTextField(useInventoryArticleStore)("code", "Inventory article code", { disabled: true })}</Grid>
                <Grid item xs={12}>{propTextField(useInventoryArticleStore)("name", "Inventory article description", { disabled: true })}</Grid>
                <Grid item xs={12}>{propTextField(useStore)("code", "Code", { disabled })}</Grid>
                <Grid item xs={12}>{propTextField(useStore)("name", "Description", { disabled })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("price", "priceFormatted", "Price", { isCurrency: true, disabled })}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("moArticle", "Is MO article?", { disabled })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("vatPercent", "vatPercentFormatted", "VAT", { unit: "%", disabled })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("moVatPercent", "moVatPercentFormatted", "MO VAT", { unit: "%", disabled })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("baseQuantity", "baseQuantityFormatted", "Base qty", { disabled })}</Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
};

interface SaleArticleEditDto extends Omit<SaleArticleDto, "id" | "codeAndName"> {
    id: number | null;
    priceFormatted?: string;
    vatPercentFormatted?: string;
    baseQuantityFormatted?: string;
    moVatPercentFormatted?: string;
}

const newSaleArticle: (articleId: number) => SaleArticleEditDto = (articleId) => ({
    id: null,
    inventoryArticleId: articleId,
    code: "",
    name: "",
    price: 0,
    baseQuantity: 1,
    vatPercent: 0,
    moVatPercent: 0,
    moArticle: false,
    isInvalid: false,
});
